import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { weatherService } from '../../../services/weatherService';

/**
 * WeatherForecastCard Component
 * Displays weather forecast for event date AND time with 5-day proximity check
 * Shows water-sport specific data: wind speed/direction, temperature, condition
 */
const WeatherForecastCard = ({ eventDate, eventTime, latitude, longitude }) => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForecast = async () => {
      if (!eventDate || !latitude || !longitude) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await weatherService?.getForecastForEventDate(
          latitude,
          longitude,
          eventDate,
          eventTime || null
        );

        setForecast(result);
      } catch (err) {
        console.error('Error fetching weather forecast:', err);
        setError('Hava durumu bilgisi alınamadı');
      } finally {
        setLoading(false);
      }
    };

    fetchForecast();
  }, [eventDate, eventTime, latitude, longitude]);

  // Get wind direction text
  const getWindDirection = (degrees) => {
    const directions = ['K', 'KD', 'D', 'GD', 'G', 'GB', 'B', 'KB'];
    const index = Math.round(((degrees % 360) / 45)) % 8;
    return directions?.[index];
  };

  // Get wind condition for SUP
  const getWindCondition = (windSpeed) => {
    if (windSpeed < 12) {
      return { text: 'İdeal', color: 'text-green-600', bgColor: 'bg-green-50' };
    } else if (windSpeed < 20) {
      return { text: 'Orta', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    } else {
      return { text: 'Zorlu', color: 'text-red-600', bgColor: 'bg-red-50' };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon name="CloudSun" size={24} className="text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Hava Durumu Tahmini</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon name="CloudSun" size={24} className="text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Hava Durumu Tahmini</h3>
        </div>
        <div className="text-center py-4">
          <Icon name="CloudOff" size={32} className="text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Too far away message
  if (forecast?.tooFarAway) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon name="CloudSun" size={24} className="text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Hava Durumu Tahmini</h3>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium mb-1">
                Hava durumu tahmini etkinlik tarihine 5 gün kala erişilebilir olacaktır.
              </p>
              <p className="text-xs text-blue-700">
                Etkinliğe {forecast?.daysUntilEvent} gün kaldı
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Past event
  if (forecast?.isPast) {
    return null;
  }

  // No forecast data
  if (!forecast) {
    return null;
  }

  const windCondition = getWindCondition(forecast?.windSpeed);

  // Format event time label
  const eventTimeLabel = forecast?.isHourlyData && forecast?.eventHour !== null
    ? `Saat ${String(forecast?.eventHour)?.padStart(2, '0')}:00 için tahmin`
    : 'Günlük tahmin';

  // Temperature display
  const tempDisplay = forecast?.temperature?.atEventTime !== undefined
    ? `${forecast?.temperature?.atEventTime}°C`
    : `${forecast?.temperature?.max}°C`;

  const tempSubDisplay = forecast?.temperature?.atEventTime !== undefined
    ? null
    : `Min: ${forecast?.temperature?.min}°C`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-100 p-2 rounded-lg">
          <Icon name="CloudSun" size={24} className="text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Hava Durumu Tahmini</h3>
          <p className="text-xs text-muted-foreground">
            {forecast?.daysUntilEvent === 0 ? 'Bugün' : `${forecast?.daysUntilEvent} gün sonra`}
            {' · '}
            <span className={forecast?.isHourlyData ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
              {eventTimeLabel}
            </span>
          </p>
        </div>
      </div>

      {/* Weather Condition */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
            <Icon 
              name={forecast?.condition?.iconName || 'Cloud'} 
              size={48} 
              className="text-blue-600" 
            />
          </div>
          <div>
            <p className="text-3xl font-bold text-foreground">
              {tempDisplay}
            </p>
            {tempSubDisplay && (
              <p className="text-sm text-muted-foreground">{tempSubDisplay}</p>
            )}
            <p className="text-sm font-medium text-foreground mt-1">
              {forecast?.condition?.description}
            </p>
          </div>
        </div>
      </div>

      {/* Water Sport Specific Data */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          SUP İçin Koşullar
        </p>

        {/* Wind Speed & Direction */}
        <div className={`${windCondition?.bgColor} rounded-lg p-4 border border-gray-200`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="Wind" size={20} className={windCondition?.color} />
              <span className="text-sm font-medium text-foreground">Rüzgar</span>
            </div>
            <span className={`text-xs font-semibold px-2 py-1 rounded ${windCondition?.color} ${windCondition?.bgColor}`}>
              {windCondition?.text}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div>
              <p className="text-2xl font-bold text-foreground">{forecast?.windSpeed} km/s</p>
              <p className="text-xs text-muted-foreground">
                {forecast?.isHourlyData ? 'Etkinlik saatindeki hız' : 'Maksimum hız'}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Icon name="Navigation" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {getWindDirection(forecast?.windDirection)}
              </span>
            </div>
          </div>
        </div>

        {/* Wave Height (if available) */}
        {forecast?.waveHeight !== null && forecast?.waveHeight !== undefined && (
          <div className="bg-cyan-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Waves" size={20} className="text-cyan-600" />
              <span className="text-sm font-medium text-foreground">Dalga Yüksekliği</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{forecast?.waveHeight} m</p>
            <p className="text-xs text-muted-foreground">Etkinlik saatindeki tahmin</p>
          </div>
        )}

        {/* Temperature Info */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Thermometer" size={20} className="text-orange-600" />
            <span className="text-sm font-medium text-foreground">Sıcaklık</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              {forecast?.temperature?.atEventTime !== undefined ? (
                <>
                  <p className="text-lg font-semibold text-foreground">
                    {forecast?.temperature?.atEventTime}°C
                  </p>
                  <p className="text-xs text-muted-foreground">Etkinlik saatindeki sıcaklık</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold text-foreground">
                    {forecast?.temperature?.max}°C / {forecast?.temperature?.min}°C
                  </p>
                  <p className="text-xs text-muted-foreground">Maksimum / Minimum</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <p className="text-xs text-blue-900">
              {forecast?.isHourlyData
                ? `Bu tahmin etkinlik saati (${String(forecast?.eventHour)?.padStart(2, '0')}:00) ve etkinlik konumu için hesaplanmıştır. Koşullar değişebilir.`
                : 'Hava durumu tahminleri değişebilir. Etkinlik günü güncel durumu kontrol edin.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherForecastCard;